import {
    type FC,
    useContext,
    useState,
    type ReactNode,
    type Dispatch,
    type SetStateAction,
    useEffect,
} from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import { useRelayEnvironment } from 'react-relay';

import dibsCss from 'dibs-css';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect.tsx';
import { Input } from 'dibs-elements/exports/Input.tsx';
import Currency from './sharedComponents/Currency.tsx';
import FormButton from './sharedComponents/FormButton.tsx';
import InputsWrapper from './sharedComponents/InputsWrapper.tsx';

import SellerFormContext from './SellerFormContext.tsx';

import { updateSellerApplicationLeadMutation } from './mutations/updateSellerApplicationLead.ts';
import { sellerApplicationFormFieldsValidation } from '../helpers/sellerApplicationFormValidator.ts';
import { trackSellerApplicationEvent } from '../helpers/sellerApplicationTracking.ts';
import {
    useGetYesNoOptions,
    useGetBusinessFocusOptions,
    useGetBusinessTypeOptions,
    useGetNumRangeOptions,
    useGetItemPriceOptions,
} from '../helpers/sellerApplicationOptions.tsx';

import {
    type BusinessDetailsKeys,
    type PrimaryBusinessFocus,
    type BusinessType,
    type EstimatedInventorySize,
    type Currency as CurrencyType,
    type SoldLastYear,
    primaryBusinessFocusMapToSF,
    estimatedInventorySizeValues,
    soldLastYearValues,
    FINE_ART,
} from './SellerApplicationFormReducer.ts';

import {
    isVariantSellerBusinessDetailsAbTest,
    SELLER_APPLICATION_BUSINESS_DETAILS,
} from '../helpers/common.ts';

export const stepTitle = (
    <FormattedMessage
        defaultMessage="Business Details"
        id="SellerApplicationForm.businessDetails.title"
    />
);

const messages = defineMessages({
    businessPrimaryFocusPlaceholder: {
        id: 'SellerApplicationForm.businessDetails.primaryBusinessFocusLabel',
        defaultMessage: 'Primary Business Focus',
    },
    businessTypePlaceholder: {
        id: 'SellerApplicationForm.businessDetails.primaryTypeLabel',
        defaultMessage: 'Business Type',
    },
    currentGalleryRepresentationPlaceholder: {
        id: 'SellerApplicationForm.businessDetails.currentGalleryRepresentationLabel',
        defaultMessage: 'Do you currently have gallery representation?',
    },
    whereRepresentedPlaceholder: {
        id: 'SellerApplicationForm.businessDetails.whereRepresentedLabel',
        defaultMessage: 'Where are you represented?',
    },
    specialtyPlaceholder: {
        id: 'SellerApplicationForm.businessDetails.specialtyLabel',
        defaultMessage: 'Please share your business specialty including top categories and styles.',
    },
    estimatedInventorySizePlaceholder: {
        id: 'SellerApplicationForm.businessDetails.estimatedInventorySizeLabel',
        defaultMessage: 'Estimated Inventory Size',
    },
    averageItemPriceValuePlaceholder: {
        id: 'SellerApplicationForm.businessDetails.averageItemPriceLabel',
        defaultMessage: 'Average Item Price',
    },
    numSoldLastYearPlaceholder: {
        id: 'SellerApplicationForm.businessDetails.numSoldLastYearLabel',
        defaultMessage: 'Approximately how many items did you sell in the last calendar year?',
    },
});

type Props = {
    setFormError: Dispatch<SetStateAction<boolean>>;
};

const SellerApplicationBusinessDetails: FC<Props> = ({ setFormError }) => {
    const environment = useRelayEnvironment();
    const intl = useIntl();
    const { state, dispatch } = useContext(SellerFormContext);
    const yesNoOptions = useGetYesNoOptions();

    const {
        primaryBusinessFocus,
        businessType,
        galleryRepresentation,
        whereRepresented,
        specialty,
        estimatedInventorySize,
        averageItemPriceCurrency,
        averageItemPriceValue,
        numSoldLastYear,
    } = state.formDataBusinessDetails;

    const [errorMessages, setErrorMessages] = useState<{
        [fieldName in BusinessDetailsKeys]?: ReactNode;
    }>({});
    const [isLoading, setIsLoading] = useState(false);

    const estimatedInventorySizeOptions = useGetNumRangeOptions(estimatedInventorySizeValues);
    const averageItemPriceValueOptions = useGetItemPriceOptions(averageItemPriceCurrency);
    const numSoldLastYearOptions = useGetNumRangeOptions(soldLastYearValues);

    useEffect(() => {
        trackAbTestV2Variant(SELLER_APPLICATION_BUSINESS_DETAILS);
    }, []);

    const validateField = (name: BusinessDetailsKeys, value?: string | boolean): void => {
        const { errors } = sellerApplicationFormFieldsValidation({ [name]: value });
        const error = errors?.[name];

        setErrorMessages({
            ...errorMessages,
            [name]: error,
        });
    };

    const showGalleryRepresentation = primaryBusinessFocus === FINE_ART;
    const showWhereGalleryRepresented = showGalleryRepresentation && galleryRepresentation;

    async function submit(): Promise<void> {
        const controlABtestFieldsToValidate = !isVariantSellerBusinessDetailsAbTest && {
            ...(showWhereGalleryRepresented && { whereRepresented }),
            specialty,
            estimatedInventorySize,
            averageItemPriceCurrency,
            averageItemPriceValue,
            numSoldLastYear,
        };

        const { isValid, errors } = sellerApplicationFormFieldsValidation({
            primaryBusinessFocus,
            businessType,
            ...(showGalleryRepresentation && { galleryRepresentation }),
            ...controlABtestFieldsToValidate,
        });
        if (isValid) {
            setErrorMessages({});
            try {
                setIsLoading(true);
                await updateSellerApplicationLeadMutation(environment, {
                    recordId: state.leadId,
                    primaryBusinessFocus: primaryBusinessFocus
                        ? primaryBusinessFocusMapToSF[primaryBusinessFocus]
                        : FINE_ART,
                    businessType,
                    galleryRepresentation,
                    whereRepresented,
                    specialty,
                    estimatedInventorySize,
                    currencyISOCode: averageItemPriceCurrency,
                    averageItemPrice: averageItemPriceValue,
                    numSoldLastYear,
                });

                trackSellerApplicationEvent({
                    type: 'professional',
                    action: 'step 2 continue',
                    interactionName: 'submit_page_2_part_1',
                    stepInteractionType: 'click on Continue',
                });

                dispatch({
                    type: 'SET_STEP',
                    step: isVariantSellerBusinessDetailsAbTest
                        ? 'BUSINESS_STEP4'
                        : 'BUSINESS_STEP3',
                });
            } catch (err) {
                setIsLoading(false);
                setFormError(true);
            }
        } else {
            setIsLoading(false);
            setErrorMessages(errors);
        }
    }

    const setFormField = (field: BusinessDetailsKeys, value?: string | boolean): void => {
        dispatch({
            type: 'SET_FORM_BUSINESS_DETAILS',
            formDataBusinessDetails: { [field]: value },
        });
    };

    const setField = (name: BusinessDetailsKeys, value?: string | boolean): void => {
        setFormField(name, value);
        validateField(name, value);
    };

    return (
        <div className={dibsCss.mbLarge} data-tn="application-form-business-details">
            <div className={dibsCss.wFull}>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="business-focus-primary"
                        name="business-focus-primary"
                        placeholder={intl.formatMessage(messages.businessPrimaryFocusPlaceholder)}
                        hasAnimatedPlaceholder
                        options={useGetBusinessFocusOptions()}
                        onChange={e => {
                            const value = e.target.value as PrimaryBusinessFocus;
                            setField('primaryBusinessFocus', value);
                            setFormField('businessType', undefined);
                        }}
                        errorMessage={errorMessages?.primaryBusinessFocus}
                        hasError={!!errorMessages?.primaryBusinessFocus}
                        disabled={isLoading}
                        value={primaryBusinessFocus}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="business-type"
                        name="business-type"
                        placeholder={intl.formatMessage(messages.businessTypePlaceholder)}
                        hasAnimatedPlaceholder
                        options={useGetBusinessTypeOptions({
                            selectedBusinessFocus: primaryBusinessFocus,
                        })}
                        onChange={e => {
                            const value = e.target.value as BusinessType;
                            setField('businessType', value);
                        }}
                        errorMessage={errorMessages?.businessType}
                        hasError={!!errorMessages?.businessType}
                        disabled={isLoading || !primaryBusinessFocus}
                        value={businessType ? businessType : ''}
                    />
                </div>
                {showGalleryRepresentation ? (
                    <>
                        <div className={dibsCss.mbSmall}>
                            <BasicSelect
                                dataTn="current-gallery-representation"
                                name="current-gallery-representation"
                                placeholder={intl.formatMessage(
                                    messages.currentGalleryRepresentationPlaceholder
                                )}
                                hasAnimatedPlaceholder
                                options={yesNoOptions}
                                onChange={e =>
                                    setField('galleryRepresentation', e.target.value === 'true')
                                }
                                errorMessage={errorMessages?.galleryRepresentation}
                                hasError={!!errorMessages?.galleryRepresentation}
                                disabled={isLoading}
                                value={
                                    typeof galleryRepresentation === 'boolean'
                                        ? String(galleryRepresentation)
                                        : undefined
                                }
                            />
                        </div>
                        {showWhereGalleryRepresented ? (
                            <div className={dibsCss.mbSmall}>
                                <Input
                                    placeholder={intl.formatMessage(
                                        messages.whereRepresentedPlaceholder
                                    )}
                                    hasAnimatedPlaceholder
                                    dataTn="where-represented"
                                    name="where-represented"
                                    onChange={e => {
                                        const value = e.target.value;
                                        setField('whereRepresented', value);
                                    }}
                                    errorMessage={errorMessages?.whereRepresented}
                                    hasError={!!errorMessages?.whereRepresented}
                                    disabled={isLoading}
                                    maxLength={255}
                                    value={whereRepresented}
                                />
                            </div>
                        ) : null}
                    </>
                ) : null}
                <div className={dibsCss.mbSmall}>
                    <Input
                        placeholder={intl.formatMessage(messages.specialtyPlaceholder)}
                        hasAnimatedPlaceholder
                        dataTn="specialty"
                        name="specialty"
                        onChange={e => {
                            const value = e.target.value;
                            setField('specialty', value);
                        }}
                        errorMessage={errorMessages?.specialty}
                        hasError={!!errorMessages?.specialty}
                        disabled={isLoading}
                        value={specialty}
                        maxLength={1000}
                    />
                </div>
                {!isVariantSellerBusinessDetailsAbTest && (
                    <>
                        <div className={dibsCss.mbSmall}>
                            <BasicSelect
                                dataTn="estimated-inventory-size"
                                name="estimated-inventory-size"
                                placeholder={intl.formatMessage(
                                    messages.estimatedInventorySizePlaceholder
                                )}
                                hasAnimatedPlaceholder
                                options={estimatedInventorySizeOptions}
                                onChange={e => {
                                    const value = e.target.value as EstimatedInventorySize;
                                    setField('estimatedInventorySize', value);
                                }}
                                errorMessage={errorMessages?.estimatedInventorySize}
                                hasError={!!errorMessages?.estimatedInventorySize}
                                disabled={isLoading}
                                value={estimatedInventorySize}
                            />
                        </div>
                        <InputsWrapper>
                            <Currency
                                dataTn="average-price-currency"
                                onChange={e => {
                                    const value = e.target.value as CurrencyType;
                                    setField('averageItemPriceCurrency', value);
                                }}
                                errorMessage={errorMessages?.averageItemPriceCurrency}
                                disabled={isLoading}
                                value={averageItemPriceCurrency || ''}
                            />
                            <BasicSelect
                                dataTn="average-price-value"
                                name="average-price-value"
                                hasAnimatedPlaceholder
                                placeholder={intl.formatMessage(
                                    messages.averageItemPriceValuePlaceholder
                                )}
                                options={averageItemPriceValueOptions}
                                onChange={e => {
                                    const value = e.target.value;
                                    setField('averageItemPriceValue', value);
                                }}
                                errorMessage={errorMessages?.averageItemPriceValue}
                                hasError={!!errorMessages?.averageItemPriceValue}
                                disabled={isLoading}
                                value={averageItemPriceValue}
                            />
                        </InputsWrapper>
                        <div className={dibsCss.mbSmall}>
                            <BasicSelect
                                dataTn="number-sold-last-year"
                                name="number-sold-last-year"
                                placeholder={intl.formatMessage(
                                    messages.numSoldLastYearPlaceholder
                                )}
                                hasAnimatedPlaceholder
                                options={numSoldLastYearOptions}
                                onChange={e => {
                                    const value = e.target.value as SoldLastYear;
                                    setField('numSoldLastYear', value);
                                }}
                                errorMessage={errorMessages?.numSoldLastYear}
                                hasError={!!errorMessages?.numSoldLastYear}
                                disabled={isLoading}
                                value={numSoldLastYear}
                            />
                        </div>
                    </>
                )}
                <FormButton isLoading={isLoading} onSubmit={submit} />
            </div>
        </div>
    );
};

export default SellerApplicationBusinessDetails;
